import { useEffect, useSyncExternalStore } from "react"
import { contactsApi, type SearchResultContact } from "@/features/meetings/services/contacts.api"

const CACHE_TTL_MS = 10 * 60 * 1000
const BATCH_WINDOW_MS = 50
const BATCH_SIZE = 50
/** How long ids from a failed lookup wait before they are asked again. */
const FAILURE_RETRY_MS = 60 * 1000

interface CacheEntry {
  /**
   * `string`: the contact's current avatar URL.
   * `null`: the server has this contact and confirms there is no picture.
   * `undefined`: the server did not return a row for this id (an id it
   * doesn't know, or one dropped by a privacy/scope filter).
   */
  url: string | null | undefined
  at: number
}

export interface AvatarCache {
  /** Synchronous read, no side effects: safe to call from a render. */
  getSnapshot: (id: string) => string | null | undefined
  /** Queue an id for the next batch unless it's already fresh in cache. */
  request: (id: string) => void
  subscribe: (onChange: () => void) => () => void
}

/**
 * A lookup failure's message carries the status, e.g. "Contact lookup failed
 * (404)" (`contactsApi.lookup`), pulled back out here rather than changing
 * that method to throw a typed error, since every other caller of it already
 * depends on the plain `Error` shape.
 */
function statusFrom(err: unknown): number | undefined {
  const message = err instanceof Error ? err.message : String(err)
  const m = /\((\d{3})\)/.exec(message)
  return m ? Number(m[1]) : undefined
}

/**
 * Factory behind the shared avatar cache, so the batching/TTL/unsupported
 * logic is testable against a fake `lookup` instead of a live server and
 * real timers.
 *
 * One request per *batch window*, not per avatar: every `AvatarPhoto` on a
 * contact list mounts in the same tick and would otherwise fire its own
 * `/contacts/lookup` call. Ids queued within `batchWindowMs` of each other
 * ride a single request (or a few, chunked at `batchSize`, the server's own
 * cap on `ids`).
 */
export function createAvatarCache(
  lookup: (ids: string[]) => Promise<SearchResultContact[]>,
  {
    ttlMs = CACHE_TTL_MS,
    batchWindowMs = BATCH_WINDOW_MS,
    batchSize = BATCH_SIZE,
    failureRetryMs = FAILURE_RETRY_MS,
  } = {},
): AvatarCache {
  const cache = new Map<string, CacheEntry>()
  const queue = new Set<string>()
  const subscribers = new Set<() => void>()
  let timer: ReturnType<typeof setTimeout> | null = null
  // Flips once and for good on a 404/405: an older server with no
  // /contacts/lookup route yet. Checked before every request and flush so one
  // bad response stops the feature for the rest of the page session instead
  // of a fresh 404 for every avatar that mounts after it.
  let unsupported = false

  function notify() {
    for (const sub of subscribers) sub()
  }

  function isFresh(entry: CacheEntry | undefined): entry is CacheEntry {
    return !!entry && Date.now() - entry.at < ttlMs
  }

  async function flushChunk(ids: string[]) {
    const now = Date.now()
    try {
      const rows = await lookup(ids)
      const byId = new Map(rows.map((r) => [r.id, r]))
      for (const id of ids) {
        const row = byId.get(id)
        // No row back for this id: cached as "unknown" (not refetched until
        // the TTL passes) rather than left open, which is what turned a
        // contact search had never found into a lookup fired on every render.
        cache.set(id, { url: row ? row.avatarUrl : undefined, at: now })
      }
    } catch (err) {
      const status = statusFrom(err)
      if (status === 404 || status === 405) {
        unsupported = true
        queue.clear()
      } else {
        // Any other failure (network blip, 500): remembered as "unknown" but
        // aged to expire after failureRetryMs instead of the full TTL. Left
        // uncached, every avatar that mounted next asked a failing server
        // again; this still retries soon, just not on every scroll.
        for (const id of ids) cache.set(id, { url: undefined, at: now - ttlMs + failureRetryMs })
      }
      notify()
      return
    }
    notify()
  }

  async function flush() {
    timer = null
    if (unsupported || queue.size === 0) return
    const ids = Array.from(queue)
    queue.clear()
    for (let i = 0; i < ids.length; i += batchSize) {
      await flushChunk(ids.slice(i, i + batchSize))
      if (unsupported) return
    }
  }

  return {
    getSnapshot(id) {
      if (unsupported) return undefined
      const entry = cache.get(id)
      return isFresh(entry) ? entry.url : undefined
    },
    request(id) {
      if (unsupported) return
      if (isFresh(cache.get(id))) return
      if (queue.has(id)) return
      queue.add(id)
      if (!timer) timer = setTimeout(() => { void flush() }, batchWindowMs)
    },
    subscribe(onChange) {
      subscribers.add(onChange)
      return () => { subscribers.delete(onChange) }
    },
  }
}

/** The one cache every `useCurrentAvatar` call shares, backed by the real API. */
const sharedCache = createAvatarCache((ids) => contactsApi.lookup(ids))

/**
 * The contact's CURRENT picture, by contactPresenceId, not the copy a
 * conversation or group took of it when it was created, which is what goes
 * on 404ing after someone changes theirs (the backend deletes the old file).
 *
 * `undefined` covers "don't know yet", "id unknown to the server" and "this
 * server has no /contacts/lookup route" alike: callers fall back to whatever
 * src they already had. `null` is a confirmed "no picture" and `string` is
 * the picture to show, live.
 */
export function useCurrentAvatar(contactId?: string | null): string | null | undefined {
  const id = contactId || undefined

  useEffect(() => {
    if (id) sharedCache.request(id)
  }, [id])

  const getSnapshot = () => (id ? sharedCache.getSnapshot(id) : undefined)
  // A couple of test suites render messenger components with
  // `renderToStaticMarkup` rather than mounting them, which routes through
  // React's server path and requires this third argument even though there
  // is no real server/client split here: nothing in the cache is ever
  // environment-specific.
  return useSyncExternalStore(sharedCache.subscribe, getSnapshot, getSnapshot)
}
